import type { IndustryType } from '@gcv/shared';
import { IndustryTypeFriendlyMap } from '@gcv/shared';

import { USStateOptions } from 'domain/consts';
import type { FiDispensaryStore } from 'stores/FiDispensaryStore';
import type { FiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import type { FiTemplateStore } from 'stores/FiTemplateStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import type { FilterListItem } from 'ui';
import { getLicenseType } from 'util/org.util';

// TO DO - clean up and add tests
export const getInitializedFilters = ({
  dispensaryStore,
  relationshipManagerStore,
  fiTemplateStore
}: {
  dispensaryStore: FiDispensaryStore;
  relationshipManagerStore: FiRelationshipManagerStore;
  fiTemplateStore: FiTemplateStore;
}) => {
  const dispensaryLicenseTypes = Array.from(
    new Set(dispensaryStore.dispensaries.map((dispensary) => getLicenseType(dispensary) || '--'))
  ).map((licenseType) => ({
    label: licenseType === '--' ? 'None' : licenseType,
    value: licenseType,
    selected: false
  }));

  const dispensaryParentAccounts = dispensaryStore.dispensaries
    .map((dispensary) => {
      const parent = dispensaryStore.bankMuos.find((m) => m.id === dispensary.muo_id);
      return {
        label: parent?.name || 'None',
        value: parent?.id || '--',
        selected: false
      };
    })
    .filter((dispensary, idx, dispensaries) => {
      const exists = dispensaries.findIndex((disp) => disp.value === dispensary.value) === idx;
      if (dispensary.label === 'None') return false;
      return exists;
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const stateOptions = JSON.parse(
    JSON.stringify(
      USStateOptions.map((state) => {
        return {
          label: state.label,
          value: state.value,
          selected: false
        };
      }).filter((state) => dispensaryStore.dispensaries.map((item) => item.state).includes(state.value))
    )
  );

  const relationshipManagerOptions = [
    {
      label: 'None',
      value: '--',
      selected: false
    },
    ...[...Object.values(relationshipManagerStore.relationshipManagers)]
      .filter((value, index, self) => value !== '--' && self.indexOf(value) === index)
      .map((rm) => {
        return {
          label: rm,
          value: rm,
          selected: false
        };
      })
  ];

  const industryTypeOptions = Array.from(
    new Set(
      [...dispensaryStore.dispensaries, ...dispensaryStore.bankMuos]
        .map((org) => ('industryType' in org ? org.industryType : undefined))
        .filter((industryType): industryType is IndustryType => !!industryType)
    )
  )
    .map((industryType) => ({
      label: IndustryTypeFriendlyMap[industryType],
      value: industryType,
      selected: false
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const templateNames = dispensaryStore.dispensaries.map((dispensary) => {
    const templateId = dispensary?.assigned_onboarding_template?.template_id;
    return getFiTemplateStore().templates.onboarding?.[templateId]?.name;
  });

  const dedupedTemplateNames = Array.from(new Set(templateNames));

  const dueDiligenceOptions = [
    {
      label: 'None',
      value: '--',
      selected: false
    },
    ...dedupedTemplateNames.map((name) => ({
      label: name,
      value: name,
      selected: false
    }))
  ];

  const filters: FilterListItem[] = [
    {
      label: 'Type of Account',
      value: 'type_of_account',
      children: [
        {
          label: 'Child Location',
          value: 'dispensary_location',
          selected: false
        },
        {
          label: 'Parent Account',
          value: 'muo',
          selected: false
        }
      ]
    },
    ...(dispensaryParentAccounts.length > 0
      ? [
          {
            label: 'Parent Account',
            value: 'parent_account',
            children: dispensaryParentAccounts
          }
        ]
      : []),
    {
      label: 'Relationship Manager',
      value: 'relationship_manager',
      children: relationshipManagerOptions
    },
    ...(industryTypeOptions.length > 1
      ? [
          {
            label: 'Industry Type',
            value: 'industry_type',
            children: industryTypeOptions
          }
        ]
      : []),
    {
      label: 'Due Diligence',
      value: 'due_diligence',
      children: dueDiligenceOptions
    },
    {
      label: 'License Type',
      value: 'license_type',
      children: dispensaryLicenseTypes
    },
    {
      label: 'State',
      value: 'state',
      children: stateOptions
    }
  ];
  return filters;
};
